import { X } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useGlossaryTerms } from '@/hooks/api'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { DefinitionPopoverProps } from '@/types/shared'
import { MarkdownRenderer } from './MarkdownRenderer'

const DefinitionPopover: React.FC<DefinitionPopoverProps> = ({
  term,
  children,
  className = '',
  side = 'top',
  align = 'center',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { data: glossaryResponse } = useGlossaryTerms({ published: true, per_page: 200 })
  const { locale, t } = useLanguage()

  // Use responsive alignment - center on mobile, prop value on desktop
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const glossaryTerm =
    term &&
    glossaryResponse?.data?.find(
      (item) =>
        (item.name && item.name.toLowerCase() === term.toLowerCase()) ||
        (item.handle && item.handle === term.toLowerCase().replace(/\s+/g, '_')),
    )

  const getLocalizedContent = (glossaryTerm: {
    name: string
    summary: string | null
    summary_de?: string | null
    summary_es?: string | null
    summary_pt?: string | null
    summary_fr?: string | null
    summary_it?: string | null
    name_de?: string | null
    name_es?: string | null
    name_pt?: string | null
    name_fr?: string | null
    name_it?: string | null
  }): { name: string; summary: string | null; showReadMore: boolean } => {
    // Use current locale from LanguageContext
    const currentLanguage = locale || 'en'

    let content: { name: string; summary: string | null }

    switch (currentLanguage) {
      case 'de':
        content = {
          name: glossaryTerm.name_de || glossaryTerm.name,
          summary: glossaryTerm.summary_de || glossaryTerm.summary,
        }
        break
      case 'es':
        content = {
          name: glossaryTerm.name_es || glossaryTerm.name,
          summary: glossaryTerm.summary_es || glossaryTerm.summary,
        }
        break
      case 'pt':
        content = {
          name: glossaryTerm.name_pt || glossaryTerm.name,
          summary: glossaryTerm.summary_pt || glossaryTerm.summary,
        }
        break
      case 'fr':
        content = {
          name: glossaryTerm.name_fr || glossaryTerm.name,
          summary: glossaryTerm.summary_fr || glossaryTerm.summary,
        }
        break
      case 'it':
        content = {
          name: glossaryTerm.name_it || glossaryTerm.name,
          summary: glossaryTerm.summary_it || glossaryTerm.summary,
        }
        break
      default:
        content = {
          name: glossaryTerm.name,
          summary: glossaryTerm.summary,
        }
    }

    // Convert custom formats to standard markdown
    if (content.summary) {
      // Convert double spaces to newlines for proper markdown formatting
      content.summary = content.summary.replace(/ {2}/g, '\n\n')

      // Convert custom image format: !/images/path(alt)! → ![alt](/images/path)
      content.summary = content.summary.replace(/!\/(images\/[^)]+)\(([^)]*)\)!/g, '![$2](/$1)')

      // Convert custom link format: "text":https://url → [text](https://url)
      content.summary = content.summary.replace(/"([^"]+)":(\S+)/g, '[$1]($2)')
    }

    // Show "Read more" link if summary is longer than 500 characters
    const showReadMore = content.summary ? content.summary.length > 500 : false

    return { ...content, showReadMore }
  }

  if (!glossaryTerm) {
    return <span className={className}>{children}</span>
  }

  const localizedContent = getLocalizedContent(glossaryTerm)
  const glossaryUrl = `/glossary#${glossaryTerm.handle || `term-${glossaryTerm.id}`}`

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <span
          className={`border-b border-dotted border-gray-400 hover:border-gray-600 hover:text-gray-900 transition-colors cursor-help text-left ${className}`}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              setIsOpen(!isOpen)
            }
          }}
          role="button"
          tabIndex={0}
        >
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-2rem)] md:w-96 p-4 bg-white shadow-lg border border-gray-200"
        side={side}
        align={isMobile ? 'center' : align}
        sideOffset={8}
        alignOffset={0}
        collisionPadding={16}
      >
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="absolute -top-2 -right-2 p-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
          <h4 className="font-semibold text-gray-900 mb-2 pr-6">{localizedContent.name}</h4>
          {localizedContent.summary ? (
            <>
              <div className="prose prose-sm max-w-none text-gray-700 max-h-[250px] overflow-y-auto pr-2 overscroll-contain touch-pan-y [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
                <MarkdownRenderer source={localizedContent.summary} highlight="" />
              </div>
              {localizedContent.showReadMore && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <a
                    href={glossaryUrl}
                    className="text-sm text-primary hover:text-primary/80 underline font-medium"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('glossary.read_more')}
                  </a>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 italic">Content coming soon</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DefinitionPopover
